import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { typeLabel } from "@/utils/format";

interface Props {
  items: Article[];
}

export default function HeroCarousel({ items }: Props) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => setActive((p) => (p + 1) % items.length), 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (dir: number) => setActive((p) => (p + dir + items.length) % items.length);

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden bg-foreground-900">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <img
            src={item.gallery[0]}
            alt={item.title}
            title={`${item.title} - 吃瓜网站`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/25" />
        </div>
      ))}

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium mb-4">
              <i className="ri-fire-fill" />
              今日头条
            </div>
            <h2 className="text-2xl md:text-4xl font-extrabold text-white leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              {items[active].title}
            </h2>
            <p className="mt-4 text-sm md:text-base text-white/80 leading-relaxed line-clamp-2 md:line-clamp-3">
              {items[active].summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${items[active].id}`}
                className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                阅读全文
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to={`/category/${items[active].category}`}
                className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-white/40 text-white text-sm font-medium hover:bg-white/10 whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-price-tag-3-line" />
                {items[active].categoryName}
              </Link>
              <span className="text-xs text-white/60 hidden sm:inline-flex items-center gap-1">
                <i className="ri-play-circle-line" />
                {typeLabel(items[active].type)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一条"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white hover:bg-white/30 cursor-pointer transition-colors"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一条"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white hover:bg-white/30 cursor-pointer transition-colors"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`第 ${i + 1} 条`}
            onClick={() => setActive(i)}
            className={`h-1.5 rounded-full cursor-pointer transition-all ${i === active ? "w-8 bg-primary-500" : "w-3 bg-white/50"}`}
          />
        ))}
      </div>
    </section>
  );
}